import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Modal from 'components/modal';
import { Content, HeroContainer } from 'components/modal/css';
import Picture from 'components/picture';
import StaticMap from 'components/static-map';
import { IUseDialogComponentProps } from 'hooks/use-dialog-modal';
import useRoutes from 'hooks/use-routes';
import { PlaceOrderButton } from 'pages/cart/place-order-button';
import { useLocationContext } from 'state/location';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { getUSState } from 'utils/restaurant';
import { routes } from 'utils/routing';

import { StyledLink, Title } from './styled';
import theme from './theme';

const Background = styled.div`
  background: ${Styles.color.white};
  width: 100%;
`;

const Inner = styled.div`
  max-width: 485px;
  width: 90%;
  margin: 0 auto;
  background: ${Styles.color.white};
  color: ${theme.innerColor};
  padding-top: 20px;
  display: flex;
  flex-direction: column;
`;

const ModalContent = styled(Content)`
  height: 100%;
`;

const LocationContainer = styled.div`
  box-shadow: ${Styles.boxShadow};
  border-radius: ${Styles.borderRadius};
  padding: 1rem 2rem;
  background: ${theme.locationContainerBackground};
`;

const StyledPlaceOrderButton = styled(PlaceOrderButton)`
  margin-top: 2rem;
`;

const StreetAddress = styled.div`
  color: ${Styles.color.black};
  font-weight: ${Styles.fontWeight.heavy};
  font-size: 1.2rem;
`;

const Address = styled.div`
  color: ${Styles.color.black};
  text-transform: capitalize;
`;

const ModalContainer = styled(Modal)`
  && {
    background: ${Styles.color.white};
    [data-reach-dialog-content] {
      background: ${Styles.color.white};
    }
    ${Styles.desktop`
      max-width: ${Styles.layout.smallerSectionMaxWidth};
    `}
  }
`;

const serviceModeMessageMap = new Map<ServiceMode, string>([
  [ServiceMode.CATERING_DELIVERY, 'storeConfirmationCateringDelivery'],
  [ServiceMode.CATERING_PICKUP, 'storeConfirmationCateringPickup'],
  [ServiceMode.CURBSIDE, 'storeConfirmationCurbside'],
  [ServiceMode.DELIVERY, 'storeConfirmationDelivery'],
  [ServiceMode.DRIVE_THRU, 'storeConfirmationDriveThru'],
  [ServiceMode.EAT_IN, 'storeConfirmationEatIn'],
  [ServiceMode.TAKEOUT, 'storeConfirmationTakeout'],
  [ServiceMode.TABLE_SERVICE, 'storeConfirmationTableService'],
]);

const StoreConfirmationModal: React.FC<IUseDialogComponentProps> = ({ onConfirm, onDismiss }) => {
  const { store } = useStoreContext();
  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { physicalAddress, restaurantImage } = store;

  const streetAddress = physicalAddress?.address1 ?? '';
  const formattedCity = physicalAddress?.city ?? '';
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = (physicalAddress?.postalCode ?? '').split('-')[0];
  const { formatMessage } = useIntl();
  const { getLocalizedRouteForPath } = useRoutes();
  const { location, setStoreLocatorCallbackUrl } = useLocationContext();
  const { serviceMode, isCatering } = useServiceModeContext();

  const chooseDifferentLocation = () => {
    shouldConfirmStoreOnCheckout(false);
    setStoreLocatorCallbackUrl(location.pathname);
  };

  const handleOnClick = () => {
    onConfirm();
  };

  const serviceModeMessage = serviceMode
    ? serviceModeMessageMap.get(serviceMode)
    : 'defaultOrderConfirmation';

  const storeLocatorPathForServiceMode = isCatering
    ? routes.cateringStoreLocator
    : getLocalizedRouteForPath('store-locator');

  return (
    <ModalContainer allowsDismiss onDismiss={onDismiss} data-testid="store-confirmation-modal">
      <HeroContainer>
        {restaurantImage ? (
          <Picture
            image={restaurantImage}
            alt={store.name ?? 'Restaurant Name Missing'}
            data-testid="store-confirmation-modal-restaurant-image"
          />
        ) : (
          <StaticMap
            data-testid="store-confirmation-modal-static-map"
            position={{
              lat: store.latitude ?? 0,
              lng: store.longitude ?? 0,
            }}
          />
        )}
      </HeroContainer>
      <ModalContent>
        <Background>
          <Inner>
            <Title tabIndex={0}>{formatMessage({ id: 'confirmYourStore' })}</Title>
            <LocationContainer
              tabIndex={0}
              aria-labelledby="store-confirmation-location street-address city-state-zip-address"
            >
              <StreetAddress id="street-address">
                {formatMessage(
                  { id: serviceModeMessage },
                  {
                    location: streetAddress,
                  }
                )}
              </StreetAddress>
              <Address id="city-state-zip-address">
                {formattedCity} {state} {postalCode}
              </Address>
            </LocationContainer>
            <StyledPlaceOrderButton confirmation onClick={handleOnClick} />

            <StyledLink
              onClick={chooseDifferentLocation}
              to={storeLocatorPathForServiceMode}
              data-testid="choose-different-location-link"
            >
              {formatMessage({ id: 'confirmationDifferentLocation' })}
            </StyledLink>
          </Inner>
        </Background>
      </ModalContent>
    </ModalContainer>
  );
};

export default StoreConfirmationModal;
